import { useId } from "react"
import { ViewListOutlined } from "@mui/icons-material"
import { Box, ToggleButton, ToggleButtonGroup, Typography, toggleButtonClasses, toggleButtonGroupClasses } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { readOnlyLabel } from "resources/ai-settings"

const CARD_SX = { border: 1, borderColor: "divider", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1.5 } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const TITLE_SX = { fontWeight: 600 } satisfies SxProps<Theme>
const BODY_SX = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 } satisfies SxProps<Theme>
// The basis is the wrap threshold: below it plus the control, the control drops under the text.
const TEXT_SX = {
  flex: (theme) => `1 1 ${theme.spacing(30)}`,
  minWidth: (theme) => theme.spacing(30),
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
} satisfies SxProps<Theme>
const MUTED_SX = { color: "text.secondary" } satisfies SxProps<Theme>
// Two separate pills, not MUI's joined segmented bar: the group's first/middle/last rules square the inner
// corners and overlap the borders, so each grouped button is reset here.
const CONTROL_SX = {
  flexShrink: 0,
  gap: 1,
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    m: 0,
    border: 1,
    borderColor: "divider",
    borderRadius: 999,
    px: 2,
    py: 0.5,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  [`& .${toggleButtonGroupClasses.grouped}.${toggleButtonClasses.selected}`]: {
    bgcolor: "var(--app-primary-filled)",
    color: "var(--app-text-on-dark-bg)",
    borderColor: "var(--app-primary-filled)",
    "&:hover": { bgcolor: "var(--app-primary-filled)" },
  },
} satisfies SxProps<Theme>

export interface OmegaPointAccessCardProps {
  readOnly: boolean
  isSaving: boolean
  onChange: (to: boolean) => void
}

export function OmegaPointAccessCard({ readOnly, isSaving, onChange }: OmegaPointAccessCardProps) {
  const labelId = useId()

  return (
    <Box sx={CARD_SX} data-testid="omega-point-access-card">
      <Box sx={HEADER_SX}>
        <ViewListOutlined fontSize="small" />
        <Typography component="h3" variant="subtitle1" sx={TITLE_SX}>Omega Point Data</Typography>
      </Box>
      <Box sx={BODY_SX}>
        <Box sx={TEXT_SX}>
          <Typography id={labelId} variant="body1">Omega Point tools and API</Typography>
          <Typography variant="body2" sx={MUTED_SX}>
            Read only means Kelly can look but never change anything. Read and write also lets it create, edit, and delete resources on your behalf.
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={readOnly}
          // Not `disabled`: disabling the focused pill drops keyboard focus out of the dock.
          aria-busy={isSaving}
          aria-labelledby={labelId}
          sx={CONTROL_SX}
          // An exclusive group reports null when the selected option is clicked again: nothing to save.
          onChange={(_, to: boolean | null) => { if (to !== null && !isSaving) onChange(to) }}
        >
          <ToggleButton value>{readOnlyLabel(true)}</ToggleButton>
          <ToggleButton value={false}>{readOnlyLabel(false)}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  )
}
